// src/lib/livret-markdown.js — Phase A.3 (PR #192, ADR-112)
//
// Parseur Markdown custom KISS ~80 LOC zéro dépendance.
//
// SÉCURITÉ — B-1 (pessimist-pr192-pre-code, BLOCKER critique) :
//   - escapeHtml() s'applique en PREMIÈRE ÉTAPE ABSOLUE sur la chaîne entière
//   - Les regex markdown travaillent ensuite uniquement sur la chaîne échappée
//   - Conséquence : `<script>` saisi par admin devient `&lt;script&gt;`,
//     impossible à transformer en balise active par les regex suivants
//
// SÉCURITÉ — B-2 (pessimist-pr192-pre-code, BLOCKER critique) :
//   - sanitizeUrl strict : whitelist http/https/mailto/tel
//   - Bypass bloqués : javascript:, data:, vbscript:, file:
//   - Élimination espaces unicode (zero-width, tab, espace non-sécable) avant test
//   - Tests obligatoires : tous protocoles dangereux + variants encoded
//
// Subset Markdown supporté (95% besoins livret) :
//   **gras**     → <strong>
//   *italique*   → <em>
//   `code`       → <code>
//   [lien](url)  → <a target="_blank" rel="noopener noreferrer">
//   - item / * item → <ul><li>
//   1. item         → <ol><li>
//   ligne vide      → <br>
//   \n simple entre deux lignes de contenu → <br> de fin de ligne (B3 recette)
//
// Hors-scope (Phase A.4+) : tableaux, images, headings #, HTML brut.

// ── Whitelist URL protocols ───────────────────────────────────────────────

// Sprint A.9.3 PR-A93-1d (ADR-145) Bug 2 : sms: ajouté pour bouton Message
// livret voyageur (LivretQuickActions). Aligné mailto:/tel: (URI scheme RFC 5724).
const ALLOWED_URL_PROTOCOLS = ["http://", "https://", "mailto:", "tel:", "sms:"];

// B-2 : protocoles strictement bloqués (whitelist NÉGATIVE explicite).
// eslint-disable-next-line no-script-url
const BLOCKED_URL_PROTOCOLS = [
  // eslint-disable-next-line no-script-url
  "javascript:",
  "data:",
  "vbscript:",
  "file:",
  "blob:",
  "ftp:",
];

/**
 * Sanitise une URL utilisateur (lien markdown).
 *
 * B-2 acté (pessimist-pr192) :
 *   1. Normalisation : suppression tous whitespace (espace ASCII, tab,
 *      espace non-sécable U+00A0, zero-width U+200B), pas seulement trim()
 *   2. Whitelist NÉGATIVE explicite (bloque javascript:/data:/vbscript:/file:)
 *      AVANT whitelist positive (defense in depth)
 *   3. Whitelist POSITIVE : http://, https://, mailto:, tel:
 *   4. Chemins relatifs / et ancres # autorisés
 *   5. Tout le reste → bloqué (retourne '')
 *
 * @param {unknown} url
 * @returns {string} URL sûre OU '' si bloquée
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== "string") return "";
  // B-2 : suppression TOUT whitespace y compris unicode (zero-width, NBSP, tab)
  const clean = url.replace(/[\s ​‌‍﻿]/g, "");
  if (!clean) return "";
  const lower = clean.toLowerCase();
  // B-2 : check blocklist EXPLICITE en premier (defense in depth)
  for (const blocked of BLOCKED_URL_PROTOCOLS) {
    if (lower.startsWith(blocked)) return "";
  }
  // Whitelist positive
  for (const allowed of ALLOWED_URL_PROTOCOLS) {
    if (lower.startsWith(allowed)) return clean;
  }
  // Relatif / ancre OK
  if (clean.startsWith("/") || clean.startsWith("#")) return clean;
  // Tout le reste → bloqué
  return "";
}

/**
 * Échappe les caractères HTML dangereux (B-1 PREMIÈRE étape parseur).
 *
 * @param {string} str
 * @returns {string}
 */
export function escapeHtml(str) {
  if (typeof str !== "string") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Parseur principal ─────────────────────────────────────────────────────

/**
 * Parse un texte Markdown subset en HTML sûr.
 *
 * Ordre strict (B-1 critique) :
 *   1. escapeHtml() sur TOUT le texte brut ABSOLUMENT EN PREMIER
 *   2. Liens [text](url) — url passe par sanitizeUrl, text reste déjà échappé
 *   3. Gras **text** et italique *text*
 *   4. Code inline `text`
 *   5. Listes - / 1. (par ligne)
 *   6. Sauts de ligne (B3 recette, spec verrouillée pessimist) :
 *      - ligne vide (line.trim() === "") → 1 <br> séparateur (lignes vides
 *        consécutives collapsées, garde anti-doublon endsWith("<br>"))
 *      - \n SIMPLE entre deux lignes de CONTENU non-vides → la ligne de
 *        contenu reçoit un <br> de fin (inséré APRÈS escapeHtml, comme
 *        l'existant — jamais sur du texte brut)
 *      - JAMAIS de <br> terminal (garde i < lines.length - 1)
 *      - PAS de <br> sur la frontière contenu→liste ni dans les <ul>/<ol>
 *      - \r (source \r\n) : conservé tel quel en fin de ligne — whitespace
 *        inerte en HTML, le <br> porte le saut de ligne
 *
 * @param {unknown} text
 * @returns {string} HTML sanitisé
 */
export function parseLivretMarkdown(text) {
  if (!text || typeof text !== "string") return "";

  // B-1 ÉTAPE 1 ABSOLUE : escape HTML sur le texte brut ENTIER
  let safe = escapeHtml(text);

  // ÉTAPE 2 : liens [text](url) — extraire URL avant son escape pour sanitizer
  // Note : la URL dans le markdown source contient potentiellement < ou > (ex.
  // [click](<javascript:alert>)). Après escapeHtml ces caractères sont
  // déjà &lt; et &gt; donc ils ne peuvent pas former une balise.
  // sanitizeUrl ré-élimine les whitespace unicode + applique whitelist.
  // On unescape l'URL juste pour la passer à sanitizeUrl puis on remet le
  // résultat échappé.
  safe = safe.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, rawUrl) => {
    // rawUrl contient potentiellement &lt; / &gt; (déjà échappés)
    // Restaurer les entités HTML pour analyse sanitizeUrl
    const unescapedUrl = rawUrl
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    const cleanUrl = sanitizeUrl(unescapedUrl);
    if (!cleanUrl) {
      // URL bloquée → rendu plain text (link text only) pour conserver le sens
      return linkText;
    }
    // Réescape l'URL nettoyée pour l'attribut href
    const safeUrl = escapeHtml(cleanUrl);
    // Liens externes : target=_blank + rel=noopener pour sécurité fenêtre
    const external = /^https?:/i.test(cleanUrl);
    const targetAttr = external
      ? ' target="_blank" rel="noopener noreferrer"'
      : "";
    return `<a href="${safeUrl}"${targetAttr}>${linkText}</a>`;
  });

  // ÉTAPE 3 : gras **text** AVANT italique (sinon *_*italique*_* tombe en gras)
  safe = safe.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

  // Italique *text* (pas si entouré de \w pour éviter d'attraper var_name)
  safe = safe.replace(/(^|[^*\w])\*([^*\n]+)\*(?![*\w])/g, "$1<em>$2</em>");

  // ÉTAPE 4 : code inline `text`
  safe = safe.replace(/`([^`\n]+)`/g, "<code>$1</code>");

  // ÉTAPE 5 : listes — découpe ligne par ligne
  const lines = safe.split("\n");
  const out = [];
  let inUl = false;
  let inOl = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const ulMatch = line.match(/^\s*[-*]\s+(.*)$/);
    const olMatch = line.match(/^\s*\d+\.\s+(.*)$/);
    if (ulMatch) {
      if (inOl) {
        out.push("</ol>");
        inOl = false;
      }
      if (!inUl) {
        out.push("<ul>");
        inUl = true;
      }
      out.push(`<li>${ulMatch[1]}</li>`);
    } else if (olMatch) {
      if (inUl) {
        out.push("</ul>");
        inUl = false;
      }
      if (!inOl) {
        out.push("<ol>");
        inOl = true;
      }
      out.push(`<li>${olMatch[1]}</li>`);
    } else {
      if (inUl) {
        out.push("</ul>");
        inUl = false;
      }
      if (inOl) {
        out.push("</ol>");
        inOl = false;
      }
      // Ligne vide → séparateur <br>
      if (line.trim() === "") {
        // Ne pas créer <br> initial / final ; on ajoute uniquement entre
        // paragraphes (lignes adjacentes vides → un seul <br>).
        if (
          out.length > 0 &&
          !out[out.length - 1].endsWith("<br>") &&
          i < lines.length - 1
        ) {
          out.push("<br>");
        }
      } else {
        // B3 recette : \n SIMPLE entre deux lignes de contenu non-vides →
        // <br> de fin de ligne (sinon le HTML collapse le \n en espace et
        // le texte s'affiche « tout collé »). Conditions (spec verrouillée) :
        //   - ligne suivante existante (garde anti-<br> terminal)
        //   - ligne suivante NON vide (au sens trim() — sinon c'est la
        //     branche ligne-vide ci-dessus qui posera SON <br>)
        //   - ligne suivante PAS un item de liste (frontière contenu→liste
        //     sans <br> ; l'intérieur des <ul>/<ol> ne passe jamais ici)
        const next = i < lines.length - 1 ? lines[i + 1] : null;
        const nextIsContent =
          next !== null &&
          next.trim() !== "" &&
          !/^\s*[-*]\s+/.test(next) &&
          !/^\s*\d+\.\s+/.test(next);
        out.push(nextIsContent ? `${line}<br>` : line);
      }
    }
  }
  // Fermer balises ouvertes en fin
  if (inUl) out.push("</ul>");
  if (inOl) out.push("</ol>");

  return out.join("\n");
}
